import "../../css/GenericComponents.css"
import React from "react";
import { IconType } from "react-icons";
import {BCIcon, ICONS} from "./BCIcons";

/**
 * Predefined button styles with background, text, and hover colors
 * 
 * Usage examples:
 * <BCMinorCTAButton buttonStyle={BUTTON_STYLES.GREEN} text="Save" onClick={handleSave} />
 * <BCMinorCTAButton buttonStyle={BUTTON_STYLES.ORANGE} text="Delete" onClick={handleDelete} />
 * <BCMinorCTAButton buttonStyle={BUTTON_STYLES.PURPLE} text="Learn More" onClick={handleLearn} />
 * 
 * Available styles:
 * - GREEN: Green theme for positive actions
 * - ORANGE: Orange theme for caution actions  
 * - PURPLE: Purple theme for informational actions
 * - PINK: Pink theme for API highlight actions
 * - BLUE: Blue theme for Discord/brand actions
 */
export const BUTTON_STYLES = {
    GREEN: {
        background: '#2e7d32',
        color: '#FFFFFF',
        hoverBackground: '#1b5e20',
        hoverShadow: '0 4px 12px rgba(46, 125, 50, 0.3)'
    },
    ORANGE: {
        background: '#e27155',
        color: '#FFFFFF',
        hoverBackground: '#d84315',
        hoverShadow: '0 4px 12px rgba(226, 113, 85, 0.3)'
    },
    PURPLE: {
        background: '#6173b9',
        color: '#FFFFFF',
        hoverBackground: '#7382b8',
        hoverShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
    },
    PINK: {
        background: '#dc2626',
        color: '#FFFFFF',
        hoverBackground: '#b91c1c',
        hoverShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
    },
    BLUE: {
        background: '#7289da',
        color: '#FFFFFF',
        hoverBackground: '#5b6eae',
        hoverShadow: '0 4px 12px rgba(114, 137, 218, 0.3)'
    }
} as const;

type ButtonStyle = typeof BUTTON_STYLES[keyof typeof BUTTON_STYLES];

export type { ButtonStyle };

interface ButtonProps {
    text: string;
    onClick: () => void;
    disabled?: boolean;
    className?: string;
    customId?: string;
    iconLeft?: IconType;
    iconRight?: IconType;
    isSelected?: boolean;
    flexShrink?: boolean;
    alignSelf?: "auto" | "flex-start" | "flex-end" | "center" | "baseline" | "stretch";
    style?: React.CSSProperties;
    // Absolute positioning support
    position?: "static" | "relative" | "absolute" | "fixed" | "sticky";
    left?: string | number;
    top?: string | number;
    right?: string | number;
    bottom?: string | number;
    zIndex?: number;
}

// Special props for BCMinorCTAButton
interface MinorCTAButtonProps extends ButtonProps {
    buttonStyle: ButtonStyle;
}

const ButtonBase: React.FC<ButtonProps & { type: "primary" | "secondary" | "underline" | "glowing" | "tertiary" | "premium" | "minorCta" }> = ({
                                                                                                 text,
                                                                                                 onClick,
                                                                                                 disabled = false,
                                                                                                 className = "",
                                                                                                 customId = "",
                                                                                                 iconLeft,
                                                                                                 iconRight,
                                                                                                 type,
                                                                                                 isSelected,
                                                                                                 flexShrink = false,
                                                                                                 alignSelf,
                                                                                                 style,
                                                                                                 position,
                                                                                                 left,
                                                                                                 top,
                                                                                                 right,
                                                                                                 bottom,
                                                                                                 zIndex
                                                                                             }) => {
    const baseClass = {
        primary: "primary-btn",
        secondary: "secondary-btn",
        underline: "underline-btn",
        glowing: "glowing-btn",
        tertiary: "tertiary-btn",
        premium: "premium-btn",
        minorCta: "minor-cta-btn",
    }[type];

    let additionalStyles: React.CSSProperties = {
        flexShrink: flexShrink ? 1 : 0,
        ...(typeof alignSelf === "string" && { alignSelf: alignSelf }),
        ...(position && { position: position }),
        ...(left !== undefined && { left: typeof left === 'number' ? `${left}em` : left }),
        ...(top !== undefined && { top: typeof top === 'number' ? `${top}em` : top }),
        ...(right !== undefined && { right: typeof right === 'number' ? `${right}em` : right }),
        ...(bottom !== undefined && { bottom: typeof bottom === 'number' ? `${bottom}em` : bottom }),
        ...(zIndex !== undefined && { zIndex: zIndex }),
        ...(style && style),
    };

    return (
        <button className={`${baseClass} ${className} ${isSelected ? 'selected' : ''}`} id={customId} onClick={onClick} disabled={disabled} style={additionalStyles}>
            <span className="button-content">
                {iconLeft && <BCIcon icon={iconLeft} className="button-icon-left" />}
                {text}
                {iconRight && <BCIcon icon={iconRight} className="button-icon-right" />}
            </span>
        </button>
    );
};

export const BCPrimaryButton = (props: ButtonProps) => <ButtonBase {...props} type="primary" />;
export const BCSecondaryButton = (props: ButtonProps) => <ButtonBase {...props} type="secondary" />;
export const BCUnderlineButton = (props: ButtonProps) => <ButtonBase {...props} type="underline" />;
export const BCTertiaryButton = (props: ButtonProps) => <ButtonBase {...props} type="tertiary" />;
export const BCPremiumButton = (props: ButtonProps) => <ButtonBase {...props} type="premium" />;
export const BCMinorCTAButton = (props: MinorCTAButtonProps) => {
    const { buttonStyle, text, onClick, disabled = false, className = "", customId = "", iconLeft, iconRight, isSelected, style, position, left, top, right, bottom, zIndex } = props;
    
    const buttonStyles: React.CSSProperties = {
        background: buttonStyle.background,
        color: buttonStyle.color,
        padding: '0.5em',
        borderRadius: '0.5em',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5em',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        ...(position && { position: position }),
        ...(left !== undefined && { left: typeof left === 'number' ? `${left}em` : left }),
        ...(top !== undefined && { top: typeof top === 'number' ? `${top}em` : top }),
        ...(right !== undefined && { right: typeof right === 'number' ? `${right}em` : right }),
        ...(bottom !== undefined && { bottom: typeof bottom === 'number' ? `${bottom}em` : bottom }),
        ...(zIndex !== undefined && { zIndex: zIndex }),
        ...(style && style),
    };

    return (
        <button
            className={`minor-cta-btn ${className} ${isSelected ? 'selected' : ''}`}
            id={customId}
            onClick={onClick}
            disabled={disabled}
            style={buttonStyles}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = buttonStyle.hoverBackground;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = buttonStyle.background;
            }}
        >
            <span className="button-content">
                {iconLeft && <BCIcon icon={iconLeft} className="button-icon-left" />}
                {text}
                <BCIcon icon={ICONS.RIGHT_ARROW} className="button-icon-right" />
            </span>
        </button>
    );
};

export const OnboardingAnswerButton: React.FC<ButtonProps> = ({
                                                                                                             text,
                                                                                                             onClick,
                                                                                                             disabled = false,
                                                                                                             className = "",
                                                                                                             customId = "",
                                                                                                             iconLeft,
                                                                                                             isSelected
                                                                                                         }) => {
    return (
        <button className={`onboarding-answer-btn ${className} ${isSelected ? 'selected' : ''}`} id={customId} onClick={onClick} disabled={disabled}>
            <span className="button-content">
                {iconLeft && <BCIcon icon={iconLeft} className="onboarding-answer-btn-icon" />}
                {text}
            </span>
        </button>
    );
};